import { minutesToSeconds, secondsToMinutes } from 'date-fns';

import type { ProfileCapabilitiesDto } from 'cadenza/map/routing/model/profile-capabilities-dto';
import { toKilometers, toMeters } from 'cadenza/map/routing/routing-sidebar/routing-modes/isochrones/calculation-options/sizes-converter';

export const TEMPORAL_ISOCHRONE_TYPE = 'TEMPORAL';
export const SPATIAL_ISOCHRONE_TYPE = 'SPATIAL';

export interface CalculationOption  {
  currentIsochroneType: string;
  profile: ProfileCapabilitiesDto;
  inputs: Inputs;
}

interface Inputs {
  zones: string;
  minutes: string;
  kilometers: string;
}

// validity
export function isValidCalculationOption ({
  currentIsochroneType,
  profile,
  inputs
}: CalculationOption) {
  const validatedZonesValue = getValidatedZonesValue(inputs.zones, profile);
  if (!validatedZonesValue) {
    return false;
  }
  const validator = currentIsochroneType === TEMPORAL_ISOCHRONE_TYPE ? areMinutesValid : areKilometersValid;
  return validator(inputs, profile, validatedZonesValue);
}

function areMinutesValid (inputs: Inputs, profile: ProfileCapabilitiesDto, validatedZonesValue: number) {
  const validatedMinutesValues = getValidatedMinutesValue(inputs.minutes, profile);
  if (!validatedMinutesValues) {
    return false;
  }
  const currentTotalMinutes = validatedMinutesValues * validatedZonesValue;
  const maxAllowedMinutesByTheProfile = secondsToMinutes(profile.maxIsochroneDuration!);  // for isochrone, the isochrone properties must be present
  return currentTotalMinutes <= maxAllowedMinutesByTheProfile;
}

function areKilometersValid (inputs: Inputs, profile: ProfileCapabilitiesDto, validatedZonesValue: number) {
  const validatedKilometersValue = getValidatedKilometersValue(inputs.kilometers, profile);
  if (!validatedKilometersValue) {
    return false;
  }
  const currentTotalKilometers = validatedKilometersValue * validatedZonesValue;
  const maxAllowedKilometersByTheProfile = toKilometers(profile.maxIsochroneDistance!);  // for isochrone, the isochrone properties must be present
  return currentTotalKilometers <= maxAllowedKilometersByTheProfile;
}

// getting validated values
/**
 * Gets the value of the zones only in case it is valid:
 * bigger than 0 and lower than the maximum specified by the profile
 *
 * @param zonesInputValue - The value set by the user in the input
 * @param profile - The current profile (it sets the constraints)
 * @return The number of the validated zones, null otherwise
 */
export function getValidatedZonesValue (zonesInputValue: string, profile: ProfileCapabilitiesDto) {
  const minimumZones = 1;
  if (!zonesInputValue) {
    return null;
  }
  // no decimals are allowed
  if (!isZonesFormatValid(zonesInputValue)) {
    return null;
  }
  const currentZones = parseInt(zonesInputValue);

  // for isochrone, the isochrone properties must be present
  return (minimumZones <= currentZones) && (currentZones <= profile.maxIsochroneZones!)
    ? currentZones
    : null;
}

function isZonesFormatValid (zonesInputValue: string) {
  return !parseFloat(zonesInputValue).toString().includes('.');
}

/**
 * Gets the value of the minutes only in case it is valid:
 * bigger than 0 and lower than the maximum specified by the profile
 *
 * @param minutesInputValue - The value set by the user in the input
 * @param profile - The current profile (it sets the constraints)
 * @return The number of the validated minutes, null otherwise
 */
export function getValidatedMinutesValue (minutesInputValue: string, profile: ProfileCapabilitiesDto) {
  const minimumMinutes = 1;
  if (!minutesInputValue) {
    return null;
  }
  // no decimals are allowed
  if (!isMinutesFormatValid(minutesInputValue)) {
    return null;
  }
  const currentMinutes = parseInt(minutesInputValue);

  // for isochrone, the isochrone properties must be present
  return (minimumMinutes <= currentMinutes) && (currentMinutes <= secondsToMinutes(profile.maxIsochroneDuration!))
    ? currentMinutes
    : null;
}

function isMinutesFormatValid (minutesInputValue: string) {
  return !parseFloat(minutesInputValue).toString().includes('.');
}

export function getValidatedSecondsValue (minutesInputValue: string, profile: ProfileCapabilitiesDto) {
  const validatedMinutes = getValidatedMinutesValue(minutesInputValue, profile);
  return validatedMinutes ? minutesToSeconds(validatedMinutes) : validatedMinutes;
}

/**
 * Gets the value of the kilometers only in case it is valid:
 * bigger than 0 and lower than the maximum specified by the profile
 *
 * @param kilometersInputValue - The value set by the user in the input
 * @param profile - The current profile (it sets the constraints)
 * @return The number of the validated minutes, null otherwise
 */
export function getValidatedKilometersValue (kilometersInputValue: string, profile: ProfileCapabilitiesDto) {
  if (!kilometersInputValue) {
    return null;
  }
  const currentKilometers = parseFloat(kilometersInputValue);

  // only two decimals are allowed
  if (!isKilometersFormatValid(currentKilometers)) {
    return null;
  }
  // for isochrone, the isochrone properties must be present
  return (currentKilometers > 0) && (currentKilometers <= toKilometers(profile.maxIsochroneDistance!))
    ? currentKilometers
    : null;
}

function isKilometersFormatValid (kilometers: number) {
  const maxDecimalsAllowed = 2;
  const arrayNumber = kilometers.toString().split('.');
  return arrayNumber.length === 1 || arrayNumber[1].length <= maxDecimalsAllowed;
}

export function getValidatedMetersValue (kilometersInputValue: string, profile: ProfileCapabilitiesDto) {
  const validatedKilometersValue = getValidatedKilometersValue(kilometersInputValue, profile);
  return validatedKilometersValue ? toMeters(validatedKilometersValue) : validatedKilometersValue;
}

// getting ranges
// profile constraints changes validation
export function getValidMinutesRangeForProfile (minutesInputValue: string, profile: ProfileCapabilitiesDto, previousProfile?: ProfileCapabilitiesDto) {
  const previousDefaultDurationInMinutes = previousProfile?.defaultIsochroneDuration ? secondsToMinutes(previousProfile.defaultIsochroneDuration) : 0;
  const value = calculateCandidateMinutesInputValue(minutesInputValue, profile, previousDefaultDurationInMinutes);
  const maxValue = secondsToMinutes(profile.maxIsochroneDuration!);// for isochrone, the isochrone properties must be present
  return {
    value: Math.min(parseInt(String(value)), maxValue),
    maxValue
  };
}

export function getValidKilometersRangeForProfile (kilometersInputValue: string, profile: ProfileCapabilitiesDto, previousProfile?: ProfileCapabilitiesDto) {
  const previousDefaultDistanceInKilometers = previousProfile?.defaultIsochroneDistance ? toKilometers(previousProfile.defaultIsochroneDistance) : 0;
  const value = calculateCandidateKilometersInputValue(kilometersInputValue, profile, previousDefaultDistanceInKilometers);
  const maxValue = toKilometers(profile.maxIsochroneDistance!);// for isochrone, the isochrone properties must be present
  return {
    value: Math.min(value, maxValue),
    maxValue
  };
}

export function getValidZonesRangeForProfile (zonesInputValue: string, defaultZones: number, profile: ProfileCapabilitiesDto) {
  const value = zonesInputValue || String(defaultZones);
  const maxValue = profile.maxIsochroneZones!;// for isochrone, the isochrone properties must be present
  return {
    value: Math.min(parseInt(value), maxValue),
    maxValue
  };
}

function calculateCandidateKilometersInputValue (kilometersInputValue: string, profile: ProfileCapabilitiesDto, previousDefaultDistanceInKilometers: number) {
  return (kilometersInputValue && parseFloat(kilometersInputValue) !== previousDefaultDistanceInKilometers)
    ? parseFloat(kilometersInputValue)
    : toKilometers(profile.defaultIsochroneDistance!);// for isochrone, the isochrone properties must be present
}

function calculateCandidateMinutesInputValue (minutesInputValue: string, profile: ProfileCapabilitiesDto, previousDefaultDurationInMinutes: number) {
  return (minutesInputValue && parseInt(minutesInputValue) !== previousDefaultDurationInMinutes)
    ? minutesInputValue
    : secondsToMinutes(profile.defaultIsochroneDuration!);// for isochrone, the isochrone properties must be present
}

// totals
export function getValidatedTotalZoneDurationInMinutes (minutesInputValue: string, zonesInputValue: string, profile: ProfileCapabilitiesDto) {
  const validatedMinutesValue = getValidatedMinutesValue(minutesInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  return validatedMinutesValue && validatedZonesValue
    ? validatedMinutesValue * validatedZonesValue
    : null;
}

export function validateDuration (minutesInputValue: string, zonesInputValue: string, profile: ProfileCapabilitiesDto) {
  const validatedSecondsValue = getValidatedSecondsValue(minutesInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  const isValid = !validatedSecondsValue
    || !validatedZonesValue
    || (validatedSecondsValue && validatedZonesValue && validatedSecondsValue * validatedZonesValue <= profile.maxIsochroneDuration!);// for isochrone, the isochrone properties must be present
  const totalZoneDurationInMinutes = validatedSecondsValue && validatedZonesValue ? secondsToMinutes(validatedSecondsValue * validatedZonesValue) : null;
  const maxIsochroneDurationInMinutes = secondsToMinutes(profile.maxIsochroneDuration!);// for isochrone, the isochrone properties must be present

  return  {
    isValid,
    totalZoneDurationInMinutes,
    maxIsochroneDurationInMinutes
  };
}

export function getValidatedTotalZoneDistanceInKilometers (kilometersInputValue: string, zonesInputValue: string, profile: ProfileCapabilitiesDto) {
  const validatedKilometersValue = getValidatedKilometersValue(kilometersInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  return validatedKilometersValue && validatedZonesValue
    ? validatedKilometersValue * validatedZonesValue
    : null;
}

export function validateExtension (kilometersInputValue: string, zonesInputValue: string, profile: ProfileCapabilitiesDto) {
  const validatedMetersValue = getValidatedMetersValue(kilometersInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  const isValid = !validatedMetersValue
    || !validatedZonesValue
    || (validatedMetersValue && validatedZonesValue && validatedMetersValue * validatedZonesValue <= profile.maxIsochroneDistance!);// for isochrone, the isochrone properties must be present
  const totalZoneExtensionInKilometers = validatedMetersValue && validatedZonesValue ? toKilometers(validatedMetersValue * validatedZonesValue) : null;
  const maxIsochroneExtensionInKilometers = toKilometers(profile.maxIsochroneDistance!);// for isochrone, the isochrone properties must be present

  return  {
    isValid,
    totalZoneExtensionInKilometers,
    maxIsochroneExtensionInKilometers
  };
}
